import { FILE_ICONS, UI_CONSTANTS } from '@/constants';
import { useIDESelectors } from '@/hooks/use-ide-selector';
import { useIDEState } from '@/hooks/use-ide-state';
import React, { useCallback, useMemo } from 'react';
import { TabContextMenu } from './tab-context-menu';

export const TabBar: React.FC = () => {
    const { setActiveTab, closeTab, closeOtherTabs } = useIDEState();
    const tabs = useIDESelectors.useTabs();
    const projects = useIDESelectors.useProjects();

    // Memoize expensive calculations
    const tabTitles = useMemo(() => {
        const titleCounts = new Map<string, number>();
        tabs.forEach(tab => {
            titleCounts.set(tab.title, (titleCounts.get(tab.title) || 0) + 1);
        });

        return tabs.map(tab => {
            const hasDuplicates = titleCounts.get(tab.title)! > 1;
            if (hasDuplicates) {
                const project = projects.find(p => p.id === tab.projectId);
                return `${project?.name}/${tab.title}`;
            }
            return tab.title;
        });
    }, [tabs, projects]);

    const truncateTitle = useCallback((title: string, maxLength: number = UI_CONSTANTS.TAB_TITLE_MAX_LENGTH) => {
        if (title.length <= maxLength) return title;
        return `...${title.slice(-(maxLength - 3))}`;
    }, []);

    const getFileIcon = useCallback((fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'html':
                return FILE_ICONS.HTML;
            default:
                return FILE_ICONS.DEFAULT;
        }
    }, []);

    const handleCloseTab = useCallback(
        (tabId: string) => () => {
            closeTab(tabId);
        },
        [closeTab]
    );

    const handleCloseOthers = useCallback(
        (tabId: string) => () => {
            closeOtherTabs(tabId);
        },
        [closeOtherTabs]
    );

    if (tabs.length === 0) {
        return null;
    }

    return (
        <div
            id='tab-bar'
            className='h-10 bg-vscode-bg-tertiary border-b border-gray-600/30 shadow-sm transition-all duration-200'
        >
            <div
                id='tab-bar-content'
                role='tablist'
                aria-label='Open files'
                className='flex items-center h-full overflow-x-auto scrollbar-hide select-none'
                onContextMenu={e => e.preventDefault()}
                onKeyDown={event => {
                    const target = event.target as HTMLElement;
                    if (
                        target.getAttribute('role') !== 'tab' ||
                        !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
                    )
                        return;
                    event.preventDefault();
                    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
                    const index = buttons.indexOf(target as HTMLButtonElement);
                    const next =
                        event.key === 'Home'
                            ? 0
                            : event.key === 'End'
                              ? tabs.length - 1
                              : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
                    setActiveTab(tabs[next].id);
                    buttons[next]?.focus();
                }}
            >
                {tabs.map((tab, index) => (
                    <TabContextMenu
                        key={tab.id}
                        onCloseTab={handleCloseTab(tab.id)}
                        onCloseOthers={handleCloseOthers(tab.id)}
                        hasMultipleTabs={tabs.length > 1}
                    >
                        <div
                            id={`tab-${tab.id}`}
                            className={`flex items-center px-2 py-1 text-sm cursor-pointer border-r border-gray-600/20 min-w-0 relative transition-colors ${
                                tab.isActive
                                    ? 'bg-[#1e1e1e] text-vscode-text border-t-4 border-t-[#007acc]'
                                    : 'bg-vscode-bg-tertiary text-vscode-text-secondary hover:bg-[#2d3238] hover:text-vscode-text'
                            }`}
                        >
                            <button
                                type='button'
                                id={`tab-label-${tab.id}`}
                                role='tab'
                                aria-selected={tab.isActive}
                                aria-controls='ide-editor-content'
                                tabIndex={tab.isActive || (!tabs.some(t => t.isActive) && index === 0) ? 0 : -1}
                                onClick={() => setActiveTab(tab.id)}
                                className='flex items-center min-h-8 min-w-0 px-1'
                            >
                                <span aria-hidden='true' className='mr-1.5 text-xs'>
                                    {getFileIcon(tab.title)}
                                </span>
                                <span className='truncate' title={tabTitles[index]}>
                                    {truncateTitle(tabTitles[index])}
                                </span>
                            </button>
                            <button
                                type='button'
                                aria-label={`Close ${tabTitles[index]}`}
                                id={`tab-close-${tab.id}`}
                                className='ml-2 text-vscode-text-muted hover:text-vscode-text min-w-8 min-h-8 rounded'
                                onClick={e => {
                                    e.stopPropagation();
                                    closeTab(tab.id);
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </TabContextMenu>
                ))}
            </div>
        </div>
    );
};
